import React, { useState, useEffect } from 'react';
import Dashboard from './Dashboard';
import Deals from './Deals';
import Contacts from './Contacts';
import NewDeal from './NewDeal';
import Lenders from './Lenders';
import QuickMatch from './QuickMatch';
import { subscribeNav, selectSection, getNav } from './dealNav';
import { useAuth } from '../auth/AuthContext';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ti-layout-dashboard' },
  { id: 'deals', label: 'Deals', icon: 'ti-files' },
  { id: 'lenders', label: 'Lenders', icon: 'ti-building-bank' },
  { id: 'contacts', label: 'Contacts', icon: 'ti-address-book' },
];

const TITLES = {
  dashboard: 'Dashboard',
  deals: 'Deals',
  contacts: 'Contacts',
  lenders: 'Lenders',
  'new-deal': 'New deal',
  'quick-match': 'Quick Match',
};

const HEAD = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

const s = {
  shell: { display: 'flex', minHeight: '100vh', background: '#F6F9FC', fontFamily: BODY, color: '#33415A' },
  sidebar: { width: '210px', background: '#fff', borderRight: '1px solid #E6EAF0', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'fixed', height: '100vh', zIndex: 100, padding: '18px 12px' },
  brand: { fontFamily: HEAD, color: '#154D97', fontSize: '15px', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', padding: '2px 8px 14px' },
  newBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%', fontFamily: HEAD, fontSize: '12.5px', fontWeight: 600, padding: '10px', background: '#154D97', color: '#fff', border: 'none', borderRadius: '9px', marginBottom: '14px', cursor: 'pointer', transition: 'background 0.15s' },
  nav: { flex: 1 },
  navItem: (active) => ({ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', padding: '9px 11px', border: 'none', borderRadius: '8px', fontSize: '13px', background: active ? '#E4EDF8' : 'transparent', color: active ? '#154D97' : '#5A6B82', fontWeight: active ? 600 : 500, marginBottom: '2px', cursor: 'pointer', fontFamily: BODY, transition: 'all 0.15s' }),
  navIcon: { fontSize: '17px', flexShrink: 0 },
  sidebarBottom: { borderTop: '1px solid #EEF1F5', paddingTop: '10px' },
  roleLine: { fontSize: '11.5px', color: '#8A94A6', padding: '6px 11px' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 11px', borderRadius: '8px', color: '#8A94A6', fontSize: '12.5px', background: 'transparent', border: 'none', fontFamily: BODY, cursor: 'pointer', transition: 'color 0.15s' },
  main: { flex: 1, marginLeft: '210px', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #E6EAF0', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 5 },
  ribbon: { background: '#fff', borderBottom: '1px solid #E6EAF0', padding: '11px 28px', display: 'flex', alignItems: 'center', gap: '14px', position: 'sticky', top: 0, zIndex: 5, overflowX: 'auto' },
  rbCell: { minWidth: '66px', flexShrink: 0 },
  rbK: { fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7189B0', whiteSpace: 'nowrap' },
  rbV: { fontSize: '14px', fontWeight: 600, color: '#2E4763', marginTop: '1px', whiteSpace: 'nowrap' },
  rbDiv: { width: '1px', alignSelf: 'stretch', background: '#E6EAF0', flexShrink: 0 },
  rbQual: { marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: '#1E7B4F', background: '#E4F3EB', border: '1px solid #CDE8D8', borderRadius: '16px', padding: '4px 11px', whiteSpace: 'nowrap', flexShrink: 0 },
  pageTitle: { fontFamily: HEAD, color: '#2E4763', fontSize: '17px', fontWeight: 600 },
  topbarRight: { color: '#A7B3C4', fontSize: '13px' },
  content: { padding: '24px 28px' },
  narrow: { maxWidth: '860px' },
  subNav: { margin: '2px 0 6px 24px', paddingLeft: '9px', borderLeft: '2px solid #EEF1F5', display: 'flex', flexDirection: 'column', gap: '1px' },
  subItem: (on) => ({ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '6px 9px', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: on ? 600 : 500, color: on ? '#154D97' : '#5A6B82', background: on ? '#E4EDF8' : 'transparent', cursor: 'pointer', fontFamily: BODY, lineHeight: 1.3 }),
  subInd: { width: '14px', display: 'flex', justifyContent: 'center', flexShrink: 0 },
  subDot: (done) => ({ width: '6px', height: '6px', borderRadius: '50%', background: done ? '#3B6D11' : '#EF9F27' }),
  subGroupLbl: { fontFamily: HEAD, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A9AB5', margin: '13px 0 5px 9px', display: 'flex', alignItems: 'center', gap: '6px' },
  subGroupIco: { fontSize: '13px', color: '#8FB0D6' },
  dealCtx: (on) => ({ margin: '8px 0 6px', background: on ? '#EAF1FA' : '#F0F5FB', border: '1px solid ' + (on ? '#BCD2EC' : '#D9E5F4'), borderRadius: '10px', padding: '9px 11px', cursor: 'pointer' }),
  dealCtxK: { fontFamily: HEAD, fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7189B0' },
  dealCtxName: { fontSize: '12px', fontWeight: 600, color: '#154D97', marginTop: '3px', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis' },
  dealCtxSub: { fontSize: '10.5px', color: '#8a9ab5', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  subDivide: { height: '1px', background: '#EEF1F5', margin: '10px 6px 4px' },
  subIco: { fontSize: '13px', color: '#8A94A6' },
};

const FULL_WIDTH = new Set(['deals', 'dashboard', 'lenders', 'contacts']);

export default function Portal({ role, onLogout }) {
  const [active, setActive] = useState('dashboard');
  const [dealsKey, setDealsKey] = useState(0);
  const [startNew, setStartNew] = useState(false);
  const { expiringSoon, minutesLeft } = useAuth();
  const [navState, setNavState] = useState(getNav());

  useEffect(() => subscribeNav(setNavState), []);

  useEffect(() => {
    if (!document.getElementById('ccco-fonts')) {
      const l = document.createElement('link');
      l.id = 'ccco-fonts';
      l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  const pageTitle = TITLES[active] || 'Dashboard';

  function renderPage() {
    switch (active) {
      case 'dashboard': return <Dashboard role={role} onNavigate={setActive} />;
      case 'quick-match': return <QuickMatch role={role} />;
      case 'deals': return <Deals key={dealsKey} role={role} startNew={startNew} />;
      case 'contacts': return <Contacts role={role} />;
      case 'lenders': return <Lenders role={role} />;
      case 'new-deal': return <NewDeal role={role} onSuccess={() => setActive('deals')} />;
      default: return <Dashboard role={role} onNavigate={setActive} />;
    }
  }

  const SessionBar = () => (!expiringSoon ? null : (

    <div style={{background:'#FAEEDA',borderBottom:'1px solid #E8D4AC',color:'#8A5A12',fontSize:'13px',padding:'9px 18px',display:'flex',alignItems:'center',gap:'12px'}}>

      <span>Your sign-in expires in about {minutesLeft} minute{minutesLeft === 1 ? '' : 's'}. Save anything open, then reload the page to sign in again.</span>

      <span style={{marginLeft:'auto',fontWeight:600,cursor:'pointer',textDecoration:'underline'}} onClick={() => window.location.reload()}>Reload now</span>

    </div>

  ));

  return (
    <div style={s.shell}>
      <div style={s.sidebar}>
        <div style={s.brand}>Crafted Capital</div>
        <button style={s.newBtn} onClick={() => { setStartNew(true); setDealsKey(k => k + 1); setActive('deals'); }}
          onMouseEnter={e => e.currentTarget.style.background = '#153F70'}
          onMouseLeave={e => e.currentTarget.style.background = '#154D97'}>
          <i className="ti ti-plus" style={{ fontSize: '15px' }} />
          New deal
        </button>
        <nav style={s.nav}>
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.id;
            return (
              <React.Fragment key={item.id}>
                <button style={s.navItem(isActive)}
                  onClick={() => { setStartNew(false); if (item.id === 'deals') setDealsKey(k => k + 1); setActive(item.id); }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F4F7FC'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
                  <i className={`ti ${item.icon}`} style={s.navIcon} />
                  {item.label}
                  {item.id === 'deals' && navState.open && <i className="ti ti-chevron-down" style={{ fontSize: '15px', marginLeft: 'auto' }} />}
                </button>
                {item.id === 'deals' && navState.open && (
                  <div style={s.subNav}>
                    {navState.context && (
                      <div style={s.dealCtx(navState.active === 'overview')} onClick={() => { setStartNew(false); setActive('deals'); selectSection('overview'); }}>
                        <div style={s.dealCtxK}>{navState.context.label || 'Viewing deal'}</div>
                        <div style={s.dealCtxName}>{navState.context.name}</div>
                        {navState.context.sub && <div style={s.dealCtxSub}>{navState.context.sub}</div>}
                      </div>
                    )}
                    {navState.sections.map((sec, i) => (
                      sec.type === 'group' ? (
                        <React.Fragment key={'g' + i}>
                          {sec.divider && <div style={s.subDivide} />}
                          <div style={s.subGroupLbl}>{sec.icon && <i className={'ti ' + sec.icon} style={s.subGroupIco} />}{sec.label}</div>
                        </React.Fragment>
                      ) : (
                        <button key={sec.key} style={s.subItem(navState.active === sec.key)}
                          onClick={() => { setStartNew(false); setActive('deals'); selectSection(sec.key); }}>
                          <span style={s.subInd}>
                            {typeof sec.done !== 'undefined'
                              ? <span style={s.subDot(!!sec.done)} />
                              : sec.icon
                                ? <i className={'ti ' + sec.icon} style={s.subIco} />
                                : null}
                          </span>
                          <span style={{ flex: 1, textAlign: 'left' }}>{sec.label}</span>
                        </button>
                      )
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
        <div style={s.sidebarBottom}>
          <div style={s.roleLine}>{role === 'admin' ? 'Admin' : 'Staff'}</div>
          <button style={s.logoutBtn} onClick={onLogout}
            onMouseEnter={e => e.currentTarget.style.color = '#154D97'}
            onMouseLeave={e => e.currentTarget.style.color = '#8A94A6'}>
            Sign out
          </button>
        </div>
      </div>
      <div style={s.main}>
        <SessionBar />
        {navState.open && active === 'deals' && navState.metrics ? (
          <div style={s.ribbon}>
            {navState.metrics.map((m, i) => (
              <React.Fragment key={m.k}>
                {i > 0 && <div style={s.rbDiv} />}
                <div style={s.rbCell}><div style={s.rbK}>{m.k}</div><div style={s.rbV}>{m.v}</div></div>
              </React.Fragment>
            ))}
            {navState.qualifies && <span style={s.rbQual}>{'\u2713'} Qualifies: {navState.qualifies}</span>}
          </div>
        ) : (
          <div style={s.topbar}>
            <div style={s.pageTitle}>{pageTitle}</div>
            <div style={s.topbarRight}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
          </div>
        )}
        <div style={s.content}>
          <div style={FULL_WIDTH.has(active) ? undefined : s.narrow}>
            {renderPage()}
          </div>
        </div>
      </div>
    </div>
  );
}
